/**
 * Centralized document utilities.
 *
 * Previously duplicated across:
 *   - QuickActions.tsx
 *   - StatusDropdown.tsx
 *   - DocumentActions.tsx
 *   - InvoiceDetails.tsx
 *   - CreditNoteDetails.tsx
 *   - DownPaymentDetails.tsx
 *
 * All document-related UI code should import helpers from here.
 */

import {
  INVOICE_STATUS_CHOICES,
  CREDIT_NOTE_STATUS_CHOICES,
  DOWN_PAYMENT_STATUS_CHOICES,
} from '@/constants/document-status';
import { ROUTES } from '@/constants/routes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DocumentType = 'invoice' | 'credit_note' | 'down_payment';

export type DocumentStatus =
  | 'draft'
  | 'sent'
  | 'issued'
  | 'paid'
  | 'applied'
  | 'partially_applied'
  | 'fully_applied'
  | 'overdue'
  | 'cancelled';

// ---------------------------------------------------------------------------
// Display names
// ---------------------------------------------------------------------------

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  invoice: 'Invoice',
  credit_note: 'Credit Note',
  down_payment: 'Down Payment',
};

/**
 * Get the human-readable name for a document type.
 */
export function getDocumentTypeName(type: DocumentType): string {
  return DOCUMENT_TYPE_LABELS[type] ?? 'Document';
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

// Semantic status chips; independent of the firm brand colour on purpose.
const NEUTRAL = 'bg-muted text-muted-foreground';
const INFO = 'bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300';
const SUCCESS = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300';
const PARTIAL = 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300';
const WARNING = 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300';
const DANGER = 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300';

const STATUS_COLOR_MAP: Record<string, string> = {
  draft: NEUTRAL,
  sent: INFO,
  issued: INFO,
  paid: SUCCESS,
  partly_paid: PARTIAL,
  applied: SUCCESS,
  partially_applied: PARTIAL,
  fully_applied: SUCCESS,
  overdue: WARNING,
  cancelled: DANGER,
};

/**
 * Return Tailwind classes for a status badge.
 */
export function getStatusColor(status: string): string {
  return STATUS_COLOR_MAP[status.toLowerCase()] ?? NEUTRAL;
}

/**
 * Format a status value for display (capitalise first letter).
 */
export function formatStatusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
}

/**
 * Get the available status choices for a given document type.
 */
export function getStatusChoices(type: DocumentType) {
  switch (type) {
    case 'invoice':
      return INVOICE_STATUS_CHOICES;
    case 'credit_note':
      return CREDIT_NOTE_STATUS_CHOICES;
    case 'down_payment':
      return DOWN_PAYMENT_STATUS_CHOICES;
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// API endpoint helpers
// ---------------------------------------------------------------------------

/**
 * Build the API endpoint for a specific document.
 */
export function getDocumentEndpoint(documentId: number): string {
  return `${ROUTES.API.INVOICING_UNIFIED}${documentId}/`;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export interface CurrencyFormatOptions {
  locale?: string;
  currency?: string;
}

const DEFAULT_CURRENCY_OPTIONS: Required<CurrencyFormatOptions> = {
  locale: 'en-BE',
  currency: 'EUR',
};

/**
 * Format an amount as currency. Accepts the string amounts the API returns;
 * anything non-numeric formats as zero.
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  options: CurrencyFormatOptions = {},
): string {
  const opts = { ...DEFAULT_CURRENCY_OPTIONS, ...options };
  const numeric = Number(amount);
  return new Intl.NumberFormat(opts.locale, {
    style: 'currency',
    currency: opts.currency,
  }).format(Number.isFinite(numeric) ? numeric : 0);
}

/**
 * Format a Date for display.
 */
export function formatDate(
  date: Date,
  locale = 'en-BE',
  style: 'full' | 'long' | 'medium' | 'short' = 'full',
): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: style }).format(date);
}
